/**
 * Cart Management JavaScript
 * main-cart.liquid 専用のカート機能
 *
 * データの流れ:
 * 1. main-cart.liquid でLiquidデータを window.CART_DATA に設定
 * 2. このスクリプトが window.CART_DATA を参照してカート機能を実行
 * 3. 他のページでは window.CART_DATA が存在しないため、このスクリプトは何もしない
 *
 * 注意点:
 * - editType は 'noshi', 'message' の値をとるが、ECナビの呼び出しは、 'message' ではなく 'msg' で行う。
 */

// ECナビ連携設定
const ECNAVI_CONFIG = {
  uid: 'UNIT-2021USTS',
  baseUrl: 'https://fdtatsujin.xbiz.jp/fw_us2/fw_login.php',
  imageBaseUrl: 'https://fdtatsujin.xbiz.jp/fw_us2/ec_file/',
  defaultParams: {
    siz: 'A4',
    pno: '1',
    mod: 'new',
    ptn: '5',
    fnc: 'dspImg',
  },
  windowOptions:
    'fullscreen=yes,location=no,scrollbars=yes,toolbar=yes,menubar=no,status=no,resizable=yes',
  testMode: false,
};

// Enhanced Cart Functionality
class ShoppingCart {
  constructor() {
    // データ可用性チェック
    if (!window.CART_DATA) {
      console.error(
        'CART_DATA が利用できません。ShoppingCartを初期化できません。',
      );
      return;
    }

    this.cartData = window.CART_DATA;
    this.form = document.querySelector('.p-cart__contents');
    // console.log('ShoppingCart初期化: フォーム要素:', this.form);
    // this.cartData.items の各アイテムの product_description は情報量が多いので除外する
    this.cartData.items.forEach((item) => {
      delete item.product_description;
    });

    console.log('カートデータ:', this.cartData);

    if (this.form) {
      console.log('フォームアクション:', this.form.action);
      console.log('フォームメソッド:', this.form.method);
    }

    this.updateButton = document.querySelector('.update-cart-btn');
    this.loadingOverlay = document.getElementById('cart-loading');
    this.init();
  }

  init() {
    this.groupCartItems(); // カタログ商品とオプション商品をグループ化
    this.setupQuantityControls();
    this.setupRemoveButtons();
    this.setupFormSubmission();
    this.setupClearCartButton();
    this.setupEditButtons();
    this.setupCopyGiftButtons(); // 新機能: のし・メッセージ設定を他のカタログ商品へ反映
    this.initializeLineItemIdentifiers(); // 新機能: 商品別識別子
    this.checkExistingPreviews();
    this.setupDeliveryDateLimits();
    this.setupWrappingSelection();
    this.hideWrappingButtonsForFuroshiki(); // 新機能: 有料風呂敷による包装紙ボタン制御
    // 初期表示時にバリデーションチェック
    this.validateCartItems();
    // デバッグ出力
    this.renderDebugOutput();
  }

  /**
   * ギフトオプション系の product_type かどうかを判定
   * gift-option, gift-option-furoshiki, gift-option-kamibukuro, gift-option-package 等
   */
  isGiftOptionType(productType) {
    return productType && productType.startsWith('gift-option');
  }

  /**
   * デバッグ用: カートデータを画面に出力
   */
  renderDebugOutput() {
    const debugOutput = document.getElementById('cart-debug-output');
    if (!debugOutput) return; // デバッグ要素がない場合はスキップ

    try {
      // カートデータを整形して表示
      const formattedData = JSON.stringify(this.cartData, null, 2);
      debugOutput.textContent = formattedData;
      console.log('カートデータ:', this.cartData);
    } catch (error) {
      debugOutput.textContent =
        'カートデータの表示に失敗しました: ' + error.message;
      console.error('デバッグ出力エラー:', error);
    }
  }

  /**
   * カート内の商品をグループ化して並び替える
   * カタログ商品（親）の直後に、そのオプション/風呂敷商品（子）を配置する
   */
  groupCartItems() {
    const tbody = document.querySelector('.p-cart__items tbody');
    if (!tbody) return;

    const allRows = Array.from(tbody.querySelectorAll('tr.p-cartItem'));
    if (allRows.length === 0) return;

    // 1. 分類: 親行・子行・単独行
    const catalogGroups = []; // { row, baseIdentifier, children: [] }
    const childRowMap = {}; // parentIdentifier -> [rows]
    const standaloneRows = [];

    allRows.forEach((row) => {
      const baseIdentifier = row.dataset.baseIdentifier;
      const parentIdentifier = row.dataset.parentCatalogIdentifier;
      const isCatalogProduct = row.dataset.isCatalogProduct === 'true';

      if (isCatalogProduct && baseIdentifier) {
        catalogGroups.push({
          row: row,
          baseIdentifier: baseIdentifier,
          children: [],
        });
      } else if (parentIdentifier) {
        if (!childRowMap[parentIdentifier]) {
          childRowMap[parentIdentifier] = [];
        }
        childRowMap[parentIdentifier].push(row);
      } else {
        standaloneRows.push(row);
      }
    });

    // 2. 子行を親行に紐づける
    catalogGroups.forEach((group) => {
      if (childRowMap[group.baseIdentifier]) {
        group.children = childRowMap[group.baseIdentifier];
        delete childRowMap[group.baseIdentifier];
      }
    });

    // 3. 孤立した子行を収集
    const orphanedChildren = [];
    Object.values(childRowMap).forEach((rows) => {
      orphanedChildren.push(...rows);
    });

    // 4. DocumentFragmentで再構築
    const fragment = document.createDocumentFragment();

    catalogGroups.forEach((group) => {
      group.row.classList.add('p-cartItem--catalog-parent');
      fragment.appendChild(group.row);

      group.children.forEach((childRow, index) => {
        childRow.classList.add('p-cartItem--catalog-child');
        if (index === group.children.length - 1) {
          childRow.classList.add('p-cartItem--catalog-child-last');
        }
        // 子行のDOM構造を変更: インデント用tdを追加し、media+textsを1つのtdにまとめる
        this.restructureChildRow(childRow);
        fragment.appendChild(childRow);
      });
    });

    standaloneRows.forEach((row) => fragment.appendChild(row));
    orphanedChildren.forEach((row) => {
      row.classList.add('p-cartItem--catalog-child', 'p-cartItem--orphaned');
      fragment.appendChild(row);
    });

    // 5. tbodyの内容を置き換え
    tbody.innerHTML = '';
    tbody.appendChild(fragment);

    console.log(
      `カートグループ化完了: ${catalogGroups.length}グループ, ${standaloneRows.length}個の単独商品`,
    );
  }

  /**
   * 子行のDOM構造を変更
   * - 先頭にインデント用の空tdを追加
   * - .p-cartItem__media と .p-cartItem__texts を1つの .p-cartItem__info tdにまとめる
   */
  restructureChildRow(row) {
    const mediaTd = row.querySelector('.p-cartItem__media');
    const textsTd = row.querySelector('.p-cartItem__texts');
    if (!mediaTd || !textsTd) return;

    // インデント用の空tdを先頭に追加
    const indentTd = document.createElement('td');
    indentTd.className = 'p-cartItem__indent';
    row.insertBefore(indentTd, row.firstChild);

    // media + texts を1つのtdにまとめる
    const infoTd = document.createElement('td');
    infoTd.className = 'p-cartItem__info';

    // mediaの中身をdivに移す
    const mediaDiv = document.createElement('div');
    mediaDiv.className = 'p-cartItem__info-media';
    while (mediaTd.firstChild) {
      mediaDiv.appendChild(mediaTd.firstChild);
    }

    // textsの中身をdivに移す
    const textsDiv = document.createElement('div');
    textsDiv.className = 'p-cartItem__info-texts';
    while (textsTd.firstChild) {
      textsDiv.appendChild(textsTd.firstChild);
    }

    infoTd.appendChild(mediaDiv);
    infoTd.appendChild(textsDiv);

    // 元のmedia/textsを削除して、indentの後にinfoを挿入
    mediaTd.remove();
    textsTd.remove();
    row.insertBefore(infoTd, indentTd.nextSibling);
  }

  setupQuantityControls() {
    const minusButtons = document.querySelectorAll(
      '.quantityInput__button--minus',
    );
    const plusButtons = document.querySelectorAll(
      '.quantityInput__button--plus',
    );
    const quantityInputs = document.querySelectorAll('input[name="updates[]"]');

    minusButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const parent = btn.closest('.quantityInput');
        const input = parent.querySelector('input[name="updates[]"]');
        const itemKey = parent.dataset.key;
        const isGiftOption =
          parent.dataset.productType?.startsWith('gift-option');

        if (input) {
          const currentValue = parseInt(input.value) || 1;

          if (currentValue > 1) {
            input.value = currentValue - 1;
            this.updateCartItem(itemKey, currentValue - 1, isGiftOption);
          } else if (currentValue === 1) {
            // If reducing from 1, remove the item
            if (confirm('この商品をカートから削除しますか？')) {
              this.updateCartItem(itemKey, 0, isGiftOption);
            }
          }
        }
      });
    });

    plusButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const parent = btn.closest('.quantityInput');
        const input = parent.querySelector('input[name="updates[]"]');
        const itemKey = parent.dataset.key;
        const isGiftOption =
          parent.dataset.productType?.startsWith('gift-option');

        if (input) {
          const currentValue = parseInt(input.value) || 0;
          const newValue = currentValue + 1;

          // オプション商品の場合、メイン商品の合計数を確認
          if (isGiftOption) {
            const mainProductTotal = this.getMainProductTotal();
            if (newValue > mainProductTotal) {
              alert(
                `オプション商品の数量はメイン商品の合計数（${mainProductTotal}個）を超えることはできません。`,
              );
              return;
            }
          }

          input.value = newValue;
          this.updateCartItem(itemKey, newValue, isGiftOption);
        }
      });
    });

    quantityInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const parent = input.closest('.quantityInput');
        const itemKey = parent.dataset.key;
        const isGiftOption =
          parent.dataset.productType?.startsWith('gift-option');
        const value = parseInt(input.value);

        if (isNaN(value) || value < 0) {
          input.value = 1;
          this.updateCartItem(itemKey, 1, isGiftOption);
        } else {
          // オプション商品の場合、メイン商品の合計数を確認
          if (isGiftOption) {
            const mainProductTotal = this.getMainProductTotal();
            if (value > mainProductTotal) {
              alert(
                `オプション商品の数量はメイン商品の合計数（${mainProductTotal}個）を超えることはできません。`,
              );
              input.value = Math.min(value, mainProductTotal);
              this.updateCartItem(itemKey, input.value, isGiftOption);
              return;
            }
          }
          this.updateCartItem(itemKey, value, isGiftOption);
        }
      });
    });
  }

  getMainProductTotal() {
    const cartItems = document.querySelectorAll('.p-cartItem');
    let mainProductCount = 0;

    cartItems.forEach((item) => {
      const quantityInput = item.querySelector('input[name="updates[]"]');
      const quantity = parseInt(quantityInput.value) || 0;
      const parent = quantityInput.closest('.quantityInput');
      const isGiftOption =
        parent.dataset.productType?.startsWith('gift-option');

      if (!isGiftOption) {
        mainProductCount += quantity;
      }
    });

    return mainProductCount;
  }

  /**
   * のし（熨斗）利用が選択されているのに、熨斗画像が作成されていないアイテムを検出する。
   * 「のし利用」を選択しただけで実際の熨斗を作成し忘れた状態でチェックアウトされるのを防ぐ。
   *
   * @returns {Array<{title: string, noshiType: string}>} 不完全なアイテムのリスト
   */
  validateNoshiCompleteness() {
    const incompleteItems = [];

    // カート内のすべてのカタログ商品（base_identifier input を持つ行）を走査
    const cartItems = document.querySelectorAll('.p-cartItem');

    cartItems.forEach((cartItem) => {
      const baseIdentifier = cartItem.dataset.baseIdentifier;
      if (!baseIdentifier) return;

      // ギフトオプション系のアイテムは除外
      const quantityInput = cartItem.querySelector('.quantityInput');
      if (
        quantityInput &&
        quantityInput.dataset.productType?.startsWith('gift-option')
      ) {
        return;
      }

      // のしタイプ input を取得
      const noshiTypeInput = document.querySelector(
        `.noshi-type-input[data-base-identifier="${baseIdentifier}"]`,
      );
      const noshiType = noshiTypeInput ? noshiTypeInput.value : '';

      // 「外のし」or「内のし」が選択されている場合のみチェック
      if (noshiType !== 'outer' && noshiType !== 'inner') return;

      // UUID input を取得
      const uuidNoshiInput = document.querySelector(
        `input[name="properties[_uuid_noshi]"][data-base-identifier="${baseIdentifier}"]`,
      );
      const uuidNoshi = uuidNoshiInput ? uuidNoshiInput.value : '';

      // のしタイプは選択されているが、UUID が空 = 熨斗画像が作成されていない
      if (!uuidNoshi || uuidNoshi === '') {
        // 商品名を取得（cart item の商品名リンクから）
        const titleEl = cartItem.querySelector('.p-cartItem__texts a');
        const title = titleEl ? titleEl.textContent.trim() : 'カタログ商品';

        incompleteItems.push({
          title: title,
          noshiType: noshiType,
        });
      }
    });

    return incompleteItems;
  }

  // 有料風呂敷が選択されているカタログ商品に「外のし」が指定されている不正状態を検出する。
  //
  // のしタイプモーダルでは風呂敷あり商品の「外のし」を disabled にしているが、
  // コピー機能（一括反映）はそのガードを迂回して outter を載せられてしまう。
  // 風呂敷の紐付けはコピーで変化しない（コピーは noshi_type/uuid のみ）ため、
  // 風呂敷は cartData、noshi_type は DOM input から読み分けて判定する
  // （openNoshiTypeModal と同じ方式）。
  validateFuroshikiOuterNoshi() {
    const invalidItems = [];
    const cartData = this.cartData ? this.cartData.items : [];
    const cartItems = document.querySelectorAll('.p-cartItem');

    cartItems.forEach((cartItem) => {
      const baseIdentifier = cartItem.dataset.baseIdentifier;
      if (!baseIdentifier) return;

      // ギフトオプション系のアイテムは除外
      const quantityInput = cartItem.querySelector('.quantityInput');
      if (
        quantityInput &&
        quantityInput.dataset.productType?.startsWith('gift-option')
      ) {
        return;
      }

      // のしタイプが「外のし」か
      const noshiTypeInput = document.querySelector(
        `.noshi-type-input[data-base-identifier="${baseIdentifier}"]`,
      );
      const noshiType = noshiTypeInput ? noshiTypeInput.value : '';
      if (noshiType !== 'outer') return;

      // この商品に有料風呂敷が紐づいているか
      const hasFuroshiki = cartData.some(
        (item) =>
          item.properties?._is_catalog_furoshiki === 'true' &&
          item.properties?._parent_catalog_identifier === baseIdentifier,
      );
      if (!hasFuroshiki) return;

      const titleEl = cartItem.querySelector('.p-cartItem__texts a');
      const title = titleEl ? titleEl.textContent.trim() : 'カタログ商品';
      invalidItems.push({ title });
    });

    return invalidItems;
  }

  setupRemoveButtons() {
    const removeButtons = document.querySelectorAll(
      '.p-cartItem__removeButton',
    );

    removeButtons.forEach((btn) => {
      btn.addEventListener(
        'click',
        async (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const cartItem = btn.closest('.p-cartItem');
          let actualCartKey = cartItem.dataset.cartItemKey;
          const baseIdentifierAttr = cartItem.dataset.baseIdentifier;

          // cart.jsからカートデータを取得
          try {
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();

            // クリックされた商品を検索
            // まず cart key で検索（通常はこれで見つかる）
            let clickedItem = cart.items.find(
              (item) => item.key === actualCartKey,
            );

            // 見つからない場合、base_identifier でフォールバック検索
            // （line item properties を更新すると cart key が変わる Shopify の仕様への対応）
            if (!clickedItem && baseIdentifierAttr) {
              clickedItem = cart.items.find(
                (item) =>
                  item.properties?._base_identifier === baseIdentifierAttr,
              );
              if (clickedItem) {
                console.log(
                  `cart key が変わっていたため、base_identifier で再検索しました: ${baseIdentifierAttr}`,
                );
                // 最新の key を DOM にも反映
                actualCartKey = clickedItem.key;
                cartItem.dataset.cartItemKey = actualCartKey;
              }
            }

            if (!clickedItem) {
              console.error('クリックされた商品が見つかりません');
              alert('エラーが発生しました。ページを再読み込みしてください。');
              return;
            }

            // カタログ商品かギフトオプション系商品かを判定
            const isCatalogProduct = !this.isGiftOptionType(
              clickedItem.product_type,
            );
            const baseIdentifier = isCatalogProduct
              ? clickedItem.properties?._base_identifier
              : null;

            // 確認メッセージ
            let confirmMessage = 'この商品をカートから削除しますか？';
            if (isCatalogProduct && baseIdentifier) {
              // 紐づいている子アイテムを数える
              const relatedCount = cart.items.filter(
                (item) =>
                  item.properties?._parent_catalog_identifier ===
                    baseIdentifier && item.key !== actualCartKey,
              ).length;

              if (relatedCount > 0) {
                confirmMessage = `この商品をカートから削除しますか？\n（紐付いているオプション商品${relatedCount}点も一緒に削除されます）`;
              }
            }

            if (confirm(confirmMessage)) {
              // 実際の削除を実行
              this.removeCartItemWithRelatedFromCart(
                cart,
                actualCartKey,
                clickedItem,
              );
            } else {
              console.log('削除がキャンセルされました');
            }
          } catch (error) {
            console.error('カートデータの取得エラー:', error);
            alert('エラーが発生しました。ページを再読み込みしてください。');
          }
        },
        true,
      ); // useCapture = true でイベントを確実にキャッチ
    });
  }

  /**
   * デバッグ用: cart.jsから取得したデータで紐づいている商品を表示
   * @param {Object} cart - cart.jsから取得したカートデータ
   * @param {string} itemKey - クリックされた商品のカートキー
   * @param {Object} clickedItem - クリックされた商品のデータ
   */
  debugRelatedItemsFromCart(cart, itemKey, clickedItem) {
    console.log('=== debugRelatedItemsFromCart開始 ===');
    console.log('クリックされた商品:', clickedItem);

    const updates = {};
    updates[itemKey] = 0; // クリックされた商品

    console.log('削除対象に追加:', itemKey);

    // カタログ商品の場合、紐付いた子アイテムを探す
    const isCatalogProduct = !this.isGiftOptionType(clickedItem.product_type);
    const baseIdentifier = clickedItem.properties?._base_identifier;

    if (isCatalogProduct && baseIdentifier) {
      console.log('カタログ商品です。base_identifier:', baseIdentifier);
      console.log('紐づいた子アイテムを探します...');

      cart.items.forEach((item, index) => {
        const itemBaseIdentifier = item.properties?._parent_catalog_identifier;
        const isGiftOption = this.isGiftOptionType(item.product_type);
        const isFuroshiki = item.properties?._is_catalog_furoshiki === 'true';

        console.log(`商品 ${index + 1}: ${item.product_title}`, {
          key: item.key,
          product_type: item.product_type,
          _parent_catalog_identifier: itemBaseIdentifier,
          _is_catalog_furoshiki: item.properties?._is_catalog_furoshiki,
          baseIdentifierMatch: itemBaseIdentifier === baseIdentifier,
          isNotSameItem: item.key !== itemKey,
          isGiftOption: isGiftOption,
          isFuroshiki: isFuroshiki,
        });

        // 同じbase_identifierを持つギフト商品を探す
        if (
          isGiftOption &&
          itemBaseIdentifier === baseIdentifier &&
          item.key !== itemKey
        ) {
          console.log('  -> 紐づいたギフト商品を発見！削除対象に追加します');
          console.log('  -> 商品名:', item.product_title);
          console.log('  -> key:', item.key);
          updates[item.key] = 0;
        }

        // 同じbase_identifierを持つ風呂敷商品を探す
        if (
          isFuroshiki &&
          itemBaseIdentifier === baseIdentifier &&
          item.key !== itemKey
        ) {
          console.log('  -> 紐づいた風呂敷商品を発見！削除対象に追加します');
          console.log('  -> 商品名:', item.product_title);
          console.log('  -> key:', item.key);
          updates[item.key] = 0;
        }
      });
    } else if (!isCatalogProduct) {
      console.log('ギフト商品です。紐づいた商品の検索はスキップします。');
    } else {
      console.log(
        'base_identifierが空です。紐づいた商品の検索はスキップします。',
      );
    }

    console.log('=== 削除対象の商品一覧 ===');
    console.log(updates);
    console.log('削除対象数:', Object.keys(updates).length);

    // 削除対象商品の詳細をリスト表示
    const itemKeys = Object.keys(updates);
    itemKeys.forEach((key, index) => {
      const item = cart.items.find((i) => i.key === key);
      if (item) {
        console.log(
          `${index + 1}. ${item.product_title} (${item.product_type})`,
        );
      }
    });

    console.log('=== debugRelatedItemsFromCart完了 ===');

    alert(
      'デバッグモード: コンソールログを確認してください。\n削除対象商品数: ' +
        Object.keys(updates).length,
    );
  }

  /**
   * デバッグ用: 紐づいている商品を表示（DOM版・旧バージョン）
   * @param {string} itemKey - クリックされた商品のカートキー
   * @param {string} baseIdentifier - 基礎識別子
   * @param {string} productType - 商品タイプ
   */
  debugRelatedItems(itemKey, baseIdentifier, productType) {
    console.log('=== debugRelatedItems開始 ===');
    console.log('対象商品:', { itemKey, baseIdentifier, productType });

    const updates = {};
    updates[itemKey] = 0; // クリックされた商品

    console.log('削除対象に追加:', itemKey);

    // base_identifierがある場合、紐付いたギフト商品も探す
    if (baseIdentifier && baseIdentifier !== '') {
      console.log('base_identifierが存在するので、紐づいた商品を探します...');
      const cartItems = document.querySelectorAll('.p-cartItem');
      console.log('カート内の商品数:', cartItems.length);

      cartItems.forEach((item, index) => {
        const itemBaseIdentifier = item.dataset.baseIdentifier;
        const itemCartKey = item.dataset.cartItemKey;
        const itemProductType = item.dataset.productType;

        console.log(`商品 ${index + 1}:`, {
          itemCartKey,
          itemBaseIdentifier,
          itemProductType,
          baseIdentifierMatch: itemBaseIdentifier === baseIdentifier,
          isNotSameItem: itemCartKey !== itemKey,
          isGiftOption: itemProductType?.startsWith('gift-option'),
        });

        // 同じbase_identifierを持つ子アイテムを探す
        if (itemBaseIdentifier === baseIdentifier && itemCartKey !== itemKey) {
          console.log('  -> base_identifierが一致しました');

          if (itemProductType?.startsWith('gift-option')) {
            console.log(
              '  -> ギフトオプション系商品です！削除対象に追加します',
            );
            updates[itemCartKey] = 0;
          } else {
            console.log(
              '  -> ギフトオプション系商品ではありません (productType:',
              itemProductType,
              ')',
            );
          }
        }
      });
    } else {
      console.log(
        'base_identifierが空です。紐づいた商品の検索はスキップします。',
      );
    }

    console.log('=== 削除対象の商品一覧 ===');
    console.log(updates);
    console.log('削除対象数:', Object.keys(updates).length);
    console.log('=== debugRelatedItems完了 ===');

    alert(
      'デバッグモード: コンソールログを確認してください。\n削除対象商品数: ' +
        Object.keys(updates).length,
    );
  }

  /**
   * カート商品と紐付いたギフト商品を削除（cart.js版）
   * @param {Object} cart - cart.jsから取得したカートデータ
   * @param {string} itemKey - 削除する商品のカートキー
   * @param {Object} clickedItem - クリックされた商品のデータ
   */
  async removeCartItemWithRelatedFromCart(cart, itemKey, clickedItem) {
    this.showLoading();

    try {
      const parentIdentifier =
        clickedItem.properties?._parent_catalog_identifier;

      // 包装オプション削除時に親カタログの対応プロパティをクリア
      const packagingClearConfig = [
        { flag: '_is_catalog_furoshiki', parentProp: '_wrapping' },
        { flag: '_is_catalog_kamibukuro', parentProp: '_kamibukuro' },
        { flag: '_is_catalog_package', parentProp: '_package' },
      ];

      if (parentIdentifier) {
        for (const { flag, parentProp } of packagingClearConfig) {
          if (clickedItem.properties?.[flag] === 'true') {
            const parentCatalog = cart.items.find(
              (item) => item.properties?._base_identifier === parentIdentifier,
            );

            if (parentCatalog && parentCatalog.properties?.[parentProp]) {
              console.log(
                `${flag} を削除 - 親カタログの ${parentProp} をクリアします`,
              );

              const clearResponse = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                },
                body: JSON.stringify({
                  id: parentCatalog.key,
                  properties: {
                    ...parentCatalog.properties,
                    [parentProp]: '',
                  },
                }),
              });

              if (!clearResponse.ok) {
                console.error(
                  `${parentProp} クリアエラー:`,
                  await clearResponse.text(),
                );
              } else {
                console.log(`${parentProp} プロパティをクリアしました`);
              }
            }
            break; // 1アイテムは1カテゴリのみ
          }
        }
      }

      // 削除処理を実行
      await this.executeRemoval(cart, itemKey, clickedItem);
    } catch (error) {
      console.error('Error removing cart items:', error);
      this.hideLoading();
      alert('カートの更新に失敗しました。ページを再読み込みしてください。');
    }
  }

  /**
   * カート商品の削除を実行（ヘルパーメソッド）
   * @param {Object} cart - カートオブジェクト
   * @param {string} itemKey - 削除する商品のカートキー
   * @param {Object} clickedItem - クリックされたアイテムオブジェクト
   */
  async executeRemoval(cart, itemKey, clickedItem) {
    // 削除対象の商品キーを収集
    const updates = {};
    updates[itemKey] = 0; // クリックされた商品

    // カタログ商品の場合、紐付いた子アイテム（オプション・風呂敷・紙袋・梱包箱）をすべて削除
    const isCatalogProduct = !this.isGiftOptionType(clickedItem.product_type);
    const baseIdentifier = clickedItem.properties?._base_identifier;

    if (isCatalogProduct && baseIdentifier) {
      cart.items.forEach((item) => {
        if (
          item.properties?._parent_catalog_identifier === baseIdentifier &&
          item.key !== itemKey
        ) {
          console.log('紐付いた子アイテムを削除:', item.product_title);
          updates[item.key] = 0;
        }
      });
    }

    // カート更新
    const updateResponse = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        updates: updates,
      }),
    });

    if (!updateResponse.ok) {
      const errorText = await updateResponse.text();
      console.error('Update response error:', errorText);
      throw new Error(
        `Cart update failed: ${updateResponse.status} - ${errorText}`,
      );
    }

    await updateResponse.json();
    window.location.reload();
  }

  /**
   * カート商品と紐付いたギフト商品を削除（DOM版・旧バージョン）
   * @param {string} itemKey - 削除する商品のカートキー
   * @param {string} baseIdentifier - 基礎識別子
   */
  async removeCartItemWithRelated(itemKey, baseIdentifier) {
    console.log('=== removeCartItemWithRelated開始 ===');
    this.showLoading();

    try {
      console.log('商品削除開始:', { itemKey, baseIdentifier });

      // 削除対象の商品キーを収集
      const updates = {};
      updates[itemKey] = 0; // クリックされた商品

      // base_identifierがある場合、紐付いたギフト商品も探す
      if (baseIdentifier && baseIdentifier !== '') {
        const cartItems = document.querySelectorAll('.p-cartItem');

        cartItems.forEach((item) => {
          const itemBaseIdentifier = item.dataset.baseIdentifier;
          const itemCartKey = item.dataset.cartItemKey;
          const itemProductType = item.dataset.productType;

          // 同じbase_identifierを持つ子アイテムを探す
          if (
            itemBaseIdentifier === baseIdentifier &&
            itemCartKey !== itemKey
          ) {
            if (itemProductType?.startsWith('gift-option')) {
              console.log('紐付いた子アイテムを削除:', itemCartKey);
              updates[itemCartKey] = 0;
            }
          }
        });
      }

      console.log('削除対象の商品:', updates);

      // カート更新
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: updates,
        }),
      });

      console.log('Update response status:', updateResponse.status);

      if (!updateResponse.ok) {
        const errorText = await updateResponse.text();
        console.error('Update response error:', errorText);
        throw new Error(
          `Cart update failed: ${updateResponse.status} - ${errorText}`,
        );
      }

      const updatedCart = await updateResponse.json();
      console.log('削除完了:', updatedCart);

      window.location.reload();
    } catch (error) {
      console.error('Error removing cart items:', error);
      this.hideLoading();
      alert('カートの更新に失敗しました。ページを再読み込みしてください。');
    }
  }

  async updateCartItem(itemKey, quantity, isGiftOption = false) {
    this.showLoading();

    try {
      console.log('Updating cart item:', { itemKey, quantity });

      // Prepare updates
      const updates = {};
      updates[itemKey] = quantity;
      console.log('Updates object:', updates);

      // Update cart items
      console.log('Updating cart items');
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: updates,
        }),
      });

      console.log('Update response status:', updateResponse.status);

      if (!updateResponse.ok) {
        const errorText = await updateResponse.text();
        console.error('Update response error:', errorText);
        throw new Error(
          `Cart update failed: ${updateResponse.status} - ${errorText}`,
        );
      }

      const updatedCart = await updateResponse.json();
      console.log('Updated cart after items update:', updatedCart);

      // カート更新後にバリデーションチェック
      this.checkCartValidationAfterUpdate(updatedCart);

      // 一時的にバリデーションチェック（リロード前）
      setTimeout(() => {
        this.validateCartItems();
      }, 100);

      window.location.reload();
    } catch (error) {
      console.error('Error updating cart:', error);
      this.hideLoading();
      alert('カートの更新に失敗しました。ページを再読み込みしてください。');
    }
  }

  checkCartValidationAfterUpdate(cart) {
    let mainProductCount = 0;
    let giftOptionCount = 0;

    // カート内の商品を集計
    cart.items.forEach((item) => {
      // product情報がない場合があるので、タイトルや他の情報から判定
      // cart.jsではproduct.typeは返されないので、別の方法で判定が必要
      // ここでは仮に、商品タイトルやハンドルに特定のキーワードが含まれるかで判定
      const isGiftOption = item.handle && item.handle.startsWith('gift-option');

      if (isGiftOption) {
        giftOptionCount += item.quantity;
      } else {
        mainProductCount += item.quantity;
      }
    });

    console.log(
      '更新後 - メイン商品数:',
      mainProductCount,
      'オプション商品数:',
      giftOptionCount,
    );
  }

  setupFormSubmission() {
    // Handle update button separately
    const updateButton = document.querySelector('button[name="update"]');
    if (updateButton) {
      updateButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.updateCart();
      });
    }

    // Make sure checkout button works normally
    const checkoutButton = document.querySelector('button[name="checkout"]');
    if (checkoutButton) {
      checkoutButton.addEventListener('click', (e) => {
        console.log('チェックアウトボタンがクリックされました');
        e.preventDefault();
        e.stopImmediatePropagation();

        // のし（熨斗）利用バリデーション:
        // 「のし利用」を選択したのに熨斗画像が作成されていない商品があればチェックアウトを阻止
        const incompleteNoshi = this.validateNoshiCompleteness();
        if (incompleteNoshi.length > 0) {
          const itemList = incompleteNoshi
            .map((item) => {
              const typeLabel =
                item.noshiType === 'outer' ? '外のし' : '内のし';
              return `・${item.title}（${typeLabel}）`;
            })
            .join('\n');
          alert(
            `以下の商品で「のし（熨斗）利用」が選択されていますが、熨斗が作成されていません：\n\n${itemList}\n\n「のし（熨斗）選択」ボタンから熨斗を作成するか、「のし（熨斗）利用」を解除してください。`,
          );
          console.warn(
            '熨斗未作成のためチェックアウトを中止:',
            incompleteNoshi,
          );
          return;
        }

        // 有料風呂敷バリデーション:
        // 有料風呂敷が選択されている商品に「外のし」が指定されている場合はチェックアウトを阻止
        // （コピー機能でのし設定を一括反映した際に発生し得る）
        const furoshikiOuter = this.validateFuroshikiOuterNoshi();
        if (furoshikiOuter.length > 0) {
          const itemList = furoshikiOuter
            .map((item) => `・${item.title}`)
            .join('\n');
          alert(
            `以下の商品は有料風呂敷が選択されているため「外のし」を指定できません：\n\n${itemList}\n\n「のし（熨斗）利用」ボタンから「内のし」を選び直すか、のし利用を解除してください。`,
          );
          console.warn(
            '有料風呂敷＋外のしのためチェックアウトを中止:',
            furoshikiOuter,
          );
          return;
        }

        if (this.form) {
          // フォームの送信先を /checkout に変更して送信
          // （送信後はページ遷移するので、action を元に戻す必要はない）
          this.form.action = '/checkout';
          console.log('チェックアウトへ送信します');
          this.form.submit();
        } else {
          console.error('フォーム要素が見つかりません');
        }
      });
    } else {
      console.error('チェックアウトボタンが見つかりません');
    }
  }

  validateCartItems() {
    console.log('=== validateCartItems開始 ===');
    const cartItems = document.querySelectorAll('.p-cartItem');
    console.log('カート商品数:', cartItems.length);
    let mainProductCount = 0;
    const giftOptionItems = [];

    // まず全てのエラークラスをクリア
    document.querySelectorAll('.quantityInput--error').forEach((el) => {
      el.classList.remove('quantityInput--error');
    });

    // カート内の商品を集計
    cartItems.forEach((item) => {
      const quantityInput = item.querySelector('input[name="updates[]"]');
      const quantity = parseInt(quantityInput.value) || 0;
      const parent = quantityInput.closest('.quantityInput');
      const isGiftOption =
        parent.dataset.productType?.startsWith('gift-option');

      if (isGiftOption) {
        // 商品名を取得
        const productNameElement = item.querySelector('.p-cartItem__texts a');
        const productName = productNameElement
          ? productNameElement.textContent.trim()
          : 'オプション商品';
        giftOptionItems.push({
          name: productName,
          quantity: quantity,
          element: item,
          quantityInput: parent,
        });
      } else {
        mainProductCount += quantity;
      }
    });

    console.log('メイン商品数合計:', mainProductCount);
    console.log('オプション商品リスト:', giftOptionItems);

    // エラーメッセージ要素
    const errorDiv = document.getElementById('cart-validation-error');
    if (!errorDiv) {
      console.log('エラーメッセージ要素が存在しません（カートが空の可能性）');
      return true; // カートが空の場合は常にバリデーション成功とする
    }
    const errorMessage = errorDiv.querySelector('.p-cart__error-message');

    // エラーメッセージを格納する配列
    const errorMessages = [];

    // 各オプション商品についてバリデーション
    giftOptionItems.forEach((giftItem) => {
      if (giftItem.quantity > mainProductCount) {
        errorMessages.push(`${giftItem.name}（${giftItem.quantity}個）`);
        // エラークラスを追加
        giftItem.quantityInput.classList.add('quantityInput--error');
      }
    });

    // エラーがある場合
    if (errorMessages.length > 0) {
      if (mainProductCount === 0) {
        errorMessage.textContent =
          'オプション商品を購入するには、メイン商品が必要です。';
      } else {
        errorMessage.innerHTML =
          `以下のオプション商品の数量がメイン商品の合計数（${mainProductCount}個）を超えています：<br>` +
          errorMessages.join('、') +
          '<br>メイン商品を追加するか、オプション商品の数量を減らしてください。';
      }
      errorDiv.style.display = 'block';
      return false;
    }

    // メイン商品が0でオプション商品がある場合
    if (mainProductCount === 0 && giftOptionItems.length > 0) {
      errorMessage.textContent =
        'オプション商品を購入するには、メイン商品が必要です。';
      errorDiv.style.display = 'block';
      // 全てのオプション商品にエラークラスを追加
      giftOptionItems.forEach((giftItem) => {
        giftItem.quantityInput.classList.add('quantityInput--error');
      });
      return false;
    }

    // バリデーション成功
    console.log('バリデーション成功: チェックアウト可能');
    errorDiv.style.display = 'none';
    return true;
  }

  async updateCart() {
    this.showLoading();

    try {
      // Get current cart data
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Prepare updates data (キーベースで取得 - DOM並び順に依存しない)
      const updates = {};
      const quantityInputs = document.querySelectorAll(
        'input[name="updates[]"]',
      );
      quantityInputs.forEach((input) => {
        const cartItemRow = input.closest('.p-cartItem');
        const itemKey = cartItemRow?.dataset.cartItemKey;
        if (itemKey) {
          updates[itemKey] = parseInt(input.value) || 0;
        }
      });

      // Clean up old UUID attributes and prepare new ones
      const newAttributes = await this.prepareCleanAttributes(cart, updates);

      // First, update cart items only
      const updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: updates,
        }),
      });

      if (!updateResponse.ok) {
        throw new Error('Cart update failed');
      }

      // Then update attributes separately if needed
      if (Object.keys(newAttributes).length > 0) {
        const attrResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            attributes: newAttributes,
          }),
        });

        if (!attrResponse.ok) {
          console.error('Failed to update attributes, but items were updated');
        }
      }

      window.location.reload();
    } catch (error) {
      console.error('Error updating cart:', error);
      this.hideLoading();
      alert('カートの更新に失敗しました。ページを再読み込みしてください。');
    }
  }

  setupClearCartButton() {
    const clearCartBtn = document.getElementById('clear-cart-btn');

    if (clearCartBtn) {
      clearCartBtn.addEventListener('click', (e) => {
        e.preventDefault();

        if (confirm('カート内のすべての商品を削除しますか？')) {
          this.clearCart();
        }
      });
    }
  }

  async clearCart() {
    this.showLoading();

    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        window.location.reload();
      } else {
        throw new Error('Cart clear failed');
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      this.hideLoading();
      // Fallback method
      window.location.href = '/cart/clear';
    }
  }

  showLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.remove('hidden');
      this.loadingOverlay.classList.add('flex');
    }
  }

  hideLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.classList.add('hidden');
      this.loadingOverlay.classList.remove('flex');
    }
  }

  // 商品別識別子機能の初期化
  async initializeLineItemIdentifiers() {
    console.log('=== 商品別識別子初期化開始 ===');

    try {
      // // カートアイテムをチェック（tr要素から）
      // const cartItems = document.querySelectorAll('.p-cartItem');
      // const baseInputs = document.querySelectorAll('.base-identifier-input');

      // console.log(`カートアイテム数: ${cartItems.length}`);
      // console.log(`基礎識別子input数: ${baseInputs.length}`);

      // // 各カートアイテムの詳細情報を確認
      // cartItems.forEach((cartItem, index) => {
      //   const baseIdentifier = cartItem.dataset.baseIdentifier;
      //   const baseInput = document.querySelector(
      //     `input[name="properties[_base_identifier]"][data-base-identifier="${baseIdentifier}"]`,
      //   );

      //   console.log(`カートアイテム ${index + 1}`);
      //   console.log(`  データ属性の基礎識別子: "${baseIdentifier}"`);
      //   console.log(
      //     `  基礎識別子input: ${baseInput ? `値="${baseInput.value}"` : '見つからず'}`,
      //   );
      // });

      // 未生成の基礎識別子を自動で生成
      await this.generateMissingBaseIdentifiers();
    } catch (error) {
      console.error('商品別識別子初期化エラー:', error);
    }

    console.log('=== 商品別識別子初期化完了 ===');
  }

  // 未生成の基礎識別子を自動で生成
  async generateMissingBaseIdentifiers() {
    const cartItems = document.querySelectorAll('.p-cartItem');

    for (const cartItem of cartItems) {
      const actualCartKey = cartItem.dataset.cartItemKey;
      const currentBaseIdentifier = cartItem.dataset.baseIdentifier;

      // gift-option系は除外
      const quantityInput = cartItem.querySelector('.quantityInput');
      if (
        quantityInput &&
        quantityInput.dataset.productType?.startsWith('gift-option')
      ) {
        continue;
      }

      // ローディングとアクション要素を取得
      const loadingDiv = document.querySelector(
        `.p-cartItem__gift-loading[data-cart-item-key="${actualCartKey}"]`,
      );
      const actionsWrapper = document.querySelector(
        `.p-cartItem__gift-actions-wrapper[data-cart-item-key="${actualCartKey}"]`,
      );

      // _base_identifierが存在するかチェック
      const baseIdentifierInput = document.querySelector(
        `input[name="properties[_base_identifier]"][data-cart-item-key="${actualCartKey}"]`,
      );

      if (
        baseIdentifierInput &&
        (!baseIdentifierInput.value || baseIdentifierInput.value === '')
      ) {
        // 基礎識別子が未設定の場合は生成（親子リンク用のブラウザUUID）
        console.log(`商品 ${actualCartKey} の基礎識別子を生成中...`);

        try {
          const newBaseIdentifier = this.generateBrowserUuid();

          // 基礎識別子をline item propertyに保存
          baseIdentifierInput.value = newBaseIdentifier;

          // データ属性も更新
          cartItem.dataset.baseIdentifier = newBaseIdentifier;

          await this.updateSingleLineItemPropertyByKey(
            actualCartKey,
            'base_identifier',
            newBaseIdentifier,
          );

          console.log(`基礎識別子生成完了: ${newBaseIdentifier}`);
        } catch (error) {
          console.error(`商品 ${actualCartKey} の識別子生成エラー:`, error);
        }
      } else if (baseIdentifierInput && baseIdentifierInput.value) {
        // 既存の基礎識別子がある場合
        console.log(`既存の基礎識別子を確認: ${baseIdentifierInput.value}`);

        // データ属性も更新
        cartItem.dataset.baseIdentifier = baseIdentifierInput.value;
      } else {
        // baseIdentifierInputが見つからない場合
        console.warn(
          `基礎識別子input要素が見つかりません: actualCartKey=${actualCartKey}`,
        );
      }

      // ローディングを非表示にして、アクションボタンを表示
      if (loadingDiv && actionsWrapper) {
        loadingDiv.style.display = 'none';
        actionsWrapper.style.display = 'flex';
      }
    }
  }

  // 新しい識別子を採番（純粋採番API + フォールバック）
  async generateNewIdentifier() {
    console.log('新しい識別子を採番中...');

    try {
      // DEBUG用
      // throw new Error('Debug error');

      const response = await fetch('/apps/cart-identifier', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}), // 空のボディ - 純粋に採番するだけ
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`採番された基礎識別子:`, data.baseIdentifier);

      return data;
    } catch (error) {
      console.warn('API採番に失敗、フォールバック処理を実行:', error.message);

      // フォールバック: フロントエンドでUUID生成
      const fallbackIdentifier = this.generateFallbackIdentifier();
      console.log(`フォールバック識別子を生成:`, fallbackIdentifier);

      return {
        baseIdentifier: fallbackIdentifier,
        message: 'Fallback identifier generated on client-side',
        isFallback: true,
      };
    }
  }

  // フォールバック用UUID生成（短縮形式）
  generateFallbackIdentifier() {
    // 現在の日時から短縮識別子を生成
    const now = new Date();
    const year = now.getFullYear().toString().slice(-1); // 年の下1桁
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const date = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    // ランダム要素を追加（衝突回避）
    const random = Math.floor(Math.random() * 1000000)
      .toString()
      .padStart(6, '0');

    // フォーマット: f + 年下1桁 + MMDD + HHMMSS + ランダム6桁
    const fallbackId = `f${year}${month}${date}${hours}${minutes}${seconds}${random}`;

    return fallbackId;
  }

  // 親子リンク用の base_identifier を生成（ブラウザUUID）
  //
  // base_identifier は「カタログ商品 ⇔ オプション商品」を紐づけるためだけのキーで、
  // 印刷物にも外部システムにも出ないため、カート内で一意なら良い。API採番は不要。
  // 通常はカタログ商品ページ（main-product-catalog.liquid）でカート追加時に付与されるが、
  // 何らかの理由で欠落していた場合の補完用にカート側でも生成できるようにしておく。
  generateBrowserUuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    // 古いブラウザ向けフォールバック（RFC4122 v4 相当）
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * 有料風呂敷が設定されているカタログ商品の「包装紙」ボタンを非表示にする
   */
  hideWrappingButtonsForFuroshiki() {
    // console.log('=== 有料風呂敷による包装紙ボタン制御開始 ===');

    const cartItems = this.cartData ? this.cartData.items : [];

    // 有料風呂敷商品のリストを取得（_is_catalog_furoshikiフラグを持つアイテム）
    const furoshikiItems = cartItems.filter(
      (item) =>
        item.properties && item.properties._is_catalog_furoshiki === 'true',
    );

    // console.log('有料風呂敷商品数:', furoshikiItems.length);

    // 各有料風呂敷商品の親カタログ識別子を取得
    furoshikiItems.forEach((furoshikiItem) => {
      const parentIdentifier =
        furoshikiItem.properties._parent_catalog_identifier;

      if (parentIdentifier) {
        // console.log('親カタログ識別子:', parentIdentifier);

        // 対応する「包装紙」ボタンを非表示にする
        const wrappingButton = document.querySelector(
          `.wrapping-select-btn[data-base-identifier="${parentIdentifier}"]`,
        );

        if (wrappingButton) {
          wrappingButton.style.display = 'none';
          // console.log('包装紙ボタンを非表示:', parentIdentifier);

          // ボタンの親要素（p-cartItem__gift-actions）を確認し、
          // その中にボタンが1つしかなく、それが非表示の場合は親要素も非表示にする
          const parentDiv = wrappingButton.closest('.p-cartItem__gift-actions');
          if (parentDiv) {
            const visibleButtons = Array.from(
              parentDiv.querySelectorAll('button'),
            ).filter((btn) => btn.style.display !== 'none');
            if (visibleButtons.length === 0) {
              parentDiv.style.display = 'none';
              // console.log('包装紙ボタンの親要素も非表示:', parentIdentifier);
            }
          }
        } else {
          // console.log('包装紙ボタンが見つかりません:', parentIdentifier);
        }
      }
    });

    // console.log('=== 有料風呂敷による包装紙ボタン制御完了 ===');
  }

  // 基礎識別子を表示・エラー表示メソッドは削除済み（デバッグエリア削除により不要）

  async updateSingleLineItemProperty(
    baseIdentifier,
    propertyName,
    propertyValue,
  ) {
    try {
      // プロパティ名にアンダースコアプレフィックスを確実に適用
      const hiddenPropertyName = propertyName.startsWith('_')
        ? propertyName
        : `_${propertyName}`;

      console.log(
        `Single line item property を更新: ${baseIdentifier}, ${hiddenPropertyName} = ${propertyValue}`,
      );

      // 現在のカート情報を取得
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        throw new Error('カート情報の取得に失敗しました');
      }
      const cart = await cartResponse.json();
      console.log(cart.items);

      // console.log('=== デバッグ: カート内のアイテム一覧 ===');
      // cart.items.forEach((item, index) => {
      //   console.log(
      //     `アイテム ${index + 1}: key = ${item.key}, title = ${item.product_title}, base_identifier = ${item.properties?._base_identifier || 'なし'}`,
      //   );
      // });
      // console.log('=== 探しているbase_identifier:', baseIdentifier, '===');

      // base_identifierで検索
      const targetItem = cart.items.find(
        (item) => item.properties?._base_identifier === baseIdentifier,
      );

      if (!targetItem) {
        console.error(
          '指定されたbase_identifierでカートアイテムが見つかりません:',
          baseIdentifier,
        );
        console.error(
          '利用可能なアイテム:',
          cart.items.map((item) => ({
            key: item.key,
            base_identifier: item.properties?._base_identifier,
          })),
        );
        throw new Error(
          `base_identifier ${baseIdentifier} を持つカートアイテムが見つかりません。`,
        );
      }

      // 見つかった商品の実際のcart keyを使用
      const actualCartKey = targetItem.key;
      console.log('対象のcart key:', actualCartKey);

      // 既存のプロパティを保持しつつ、新しいプロパティを追加/更新
      const properties = targetItem.properties
        ? { ...targetItem.properties }
        : {};
      properties[hiddenPropertyName] = propertyValue;

      console.log('更新後のプロパティ:', properties);

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: actualCartKey,
          properties: properties,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorText}`,
        );
      }

      const updatedCart = await response.json();
      console.log('Single line item property を更新しました');
      return updatedCart;
    } catch (error) {
      console.error('Error updating single line item property:', error);
      throw error;
    }
  }

  // cart key を指定して単一プロパティを更新（既存プロパティは保持）。
  // base_identifier をこれから付与する等、base_identifier で検索できない場合に使う。
  async updateSingleLineItemPropertyByKey(
    actualCartKey,
    propertyName,
    propertyValue,
  ) {
    const hiddenPropertyName = propertyName.startsWith('_')
      ? propertyName
      : `_${propertyName}`;

    const cartResponse = await fetch('/cart.js');
    if (!cartResponse.ok) {
      throw new Error('カート情報の取得に失敗しました');
    }
    const cart = await cartResponse.json();
    const targetItem = cart.items.find((item) => item.key === actualCartKey);
    if (!targetItem) {
      throw new Error(`cart key ${actualCartKey} のアイテムが見つかりません。`);
    }

    const properties = targetItem.properties
      ? { ...targetItem.properties }
      : {};
    properties[hiddenPropertyName] = propertyValue;

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: actualCartKey, properties }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    return await response.json();
  }

  async updateLineItemProperties(actualCartKey, properties) {
    try {
      console.log('Line item properties を更新:', actualCartKey, properties);

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: actualCartKey,
          properties: properties,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedCart = await response.json();
      console.log('Line item properties を更新しました:', updatedCart);
      return updatedCart;
    } catch (error) {
      console.error('Error updating line item properties:', error);
      throw error;
    }
  }

  async updateAllLineItemProperties() {
    try {
      // インスタンス変数からカートアイテムを取得
      const cartItems = this.cartData ? this.cartData.items : [];

      for (const item of cartItems) {
        if (this.isGiftOptionType(item.product_type)) continue;

        const properties = {};
        let hasChanges = false;

        // このアイテムのすべてのプロパティ用inputを取得
        const propertyInputs = document.querySelectorAll(
          `[data-cart-item-key="${item.key}"]`,
        );

        propertyInputs.forEach((input) => {
          if (input.name && input.name.startsWith('properties[')) {
            const propertyName = input.name
              .replace('properties[', '')
              .replace(']', '');
            if (input.value && input.value.trim() !== '') {
              properties[propertyName] = input.value;
              hasChanges = true;
            }
            // 既存のプロパティを保持（空でない限り）
            else if (item.properties && item.properties[propertyName]) {
              properties[propertyName] = item.properties[propertyName];
              hasChanges = true;
            }
          }
        });

        if (hasChanges) {
          await this.updateLineItemProperties(item.key, properties);
        }
      }

      console.log('すべてのline item propertiesを更新しました');
    } catch (error) {
      console.error('Error updating all line item properties:', error);
    }
  }

  setupEditButtons() {
    const noshiButtons = document.querySelectorAll('.noshi-edit-btn');
    const messageButtons = document.querySelectorAll('.message-edit-btn');
    const noshiDeleteButtons = document.querySelectorAll('.noshi-delete-btn');
    const messageDeleteButtons = document.querySelectorAll(
      '.message-delete-btn',
    );
    const noshiTypeButtons = document.querySelectorAll(
      '.noshi-type-select-btn',
    );

    // console.log(
    //   '###################### setupEditButtons ##########################',
    // );
    // console.log('のしボタン数:', noshiButtons.length);
    // console.log('メッセージボタン数:', messageButtons.length);
    // console.log('のし削除ボタン数:', noshiDeleteButtons.length);
    // console.log('メッセージ削除ボタン数:', messageDeleteButtons.length);
    // console.log('のしタイプボタン数:', noshiTypeButtons.length);

    // 各ボタンの詳細をログ出力
    // noshiButtons.forEach((btn, index) => {
    //   console.log(`のしボタン ${index}:`, btn.dataset);
    // });

    // のし（熨斗）利用ボタン
    noshiTypeButtons.forEach((btn) => {
      // console.log('のし（熨斗）利用ボタンにイベントリスナー追加:', btn.dataset);
      btn.addEventListener('click', (e) => {
        // console.log('のし（熨斗）利用ボタンクリック:', e.target);
        // ボタン要素自体を渡して、baseIdentifierを使用できるようにする
        this.openNoshiTypeModal(btn);
      });
    });

    // 熨斗選択ボタン
    noshiButtons.forEach((btn) => {
      // console.log('のし選択ボタンにイベントリスナー追加:', btn.dataset);
      btn.addEventListener('click', (e) => {
        console.log('のし選択ボタンクリック:', e.target);
        this.openExternalEditor(btn, 'noshi');
      });
    });

    // メッセージカード編集ボタン
    messageButtons.forEach((btn) => {
      // console.log('メッセージボタンにイベントリスナー追加:', btn.dataset);
      btn.addEventListener('click', (e) => {
        // console.log('メッセージボタンクリック:', e.target);
        this.openExternalEditor(btn, 'msg');
      });
    });

    // 熨斗削除ボタン
    noshiDeleteButtons.forEach((btn) => {
      // console.log('のし（熨斗）削除ボタンにイベントリスナー追加:', btn.dataset);
      btn.addEventListener('click', (e) => {
        // console.log('のし（熨斗）削除ボタンクリック:', e.target);
        this.deleteGiftOption(btn, 'noshi');
      });
    });

    // メッセージカード削除ボタン
    messageDeleteButtons.forEach((btn) => {
      // console.log('メッセージ削除ボタンにイベントリスナー追加:', btn.dataset);
      btn.addEventListener('click', (e) => {
        // console.log('メッセージ削除ボタンクリック:', e.target);
        this.deleteGiftOption(btn, 'msg');
      });
    });

    // ページ読み込み時に既存のプレビューをチェック
    this.checkExistingPreviews();
  }

  // ── のし・メッセージ設定の一括反映（コピー）機能 ─────────────────────────
  //
  // カート内に複数のカタログ商品がある場合、ある商品で指定した
  // 「のし利用（_noshi_type）」「のし（_uuid_noshi）」「メッセージカード（_uuid_message）」を
  // 他のすべてのカタログ商品にコピーする。包装紙・紙袋等の包装系はコピー対象外。
  //
  // コピー先は識別子をそのまま共有するため、外部システム（筆の達人）上は同じ画像を指す。
  // コピー先で「変更」すると新IDが採番されて分岐する（openExternalEditor 参照）。
  setupCopyGiftButtons() {
    const copyButtons = document.querySelectorAll('.copy-gift-to-all-btn');
    copyButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.copyGiftSettingsToAll(btn);
      });
    });

    // カタログ商品が1つしかない場合は反映ボタンを隠す（反映先がないため）
    const catalogButtons = document.querySelectorAll('.copy-gift-to-all-btn');
    if (catalogButtons.length <= 1) {
      catalogButtons.forEach((btn) => {
        const wrapper = btn.closest('.p-cartItem__gift-copy');
        if (wrapper) wrapper.style.display = 'none';
      });
    }
  }

  // カタログ商品のプロパティを base_identifier で取得（DOM の hidden input を正とする）
  getCatalogPropsByBase(baseIdentifier) {
    const get = (cls) => {
      const el = document.querySelector(
        `.${cls}[data-base-identifier="${baseIdentifier}"]`,
      );
      return el ? el.value || '' : '';
    };
    return {
      noshiType: get('noshi-type-input'),
      uuidNoshi: get('uuid-noshi-input'),
      uuidMessage: get('uuid-message-input'),
    };
  }

  async copyGiftSettingsToAll(button) {
    const sourceBase = button.dataset.baseIdentifier;
    if (!sourceBase) {
      alert('エラーが発生しました。ページを再読み込みしてください。');
      return;
    }

    // コピー元の設定を取得
    const source = this.getCatalogPropsByBase(sourceBase);

    // コピー元に何も指定がなければ何もしない
    if (!source.noshiType && !source.uuidNoshi && !source.uuidMessage) {
      alert(
        'この商品に、のし・メッセージカードの指定がありません。\n先にこの商品で指定してから反映してください。',
      );
      return;
    }

    // 反映先のカタログ商品（自分以外）を収集
    const targets = Array.from(
      document.querySelectorAll('.copy-gift-to-all-btn'),
    )
      .map((btn) => btn.dataset.baseIdentifier)
      .filter((base) => base && base !== sourceBase);

    if (targets.length === 0) {
      alert('反映先のカタログ商品がありません。');
      return;
    }

    // 確認モーダル
    const confirmed = await this.showReflectConfirmModal(source);
    if (!confirmed) return;

    this.showLoading();
    try {
      for (const targetBase of targets) {
        // 1) hidden input を更新
        this.setCatalogInputValue(
          targetBase,
          'noshi-type-input',
          source.noshiType,
        );
        this.setCatalogInputValue(
          targetBase,
          'uuid-noshi-input',
          source.uuidNoshi,
        );
        this.setCatalogInputValue(
          targetBase,
          'uuid-message-input',
          source.uuidMessage,
        );

        // 2) Line Item Properties を一括更新（3項目を1リクエストで）
        await this.updateMultipleLineItemProperties(targetBase, {
          _noshi_type: source.noshiType,
          _uuid_noshi: source.uuidNoshi,
          _uuid_message: source.uuidMessage,
        });

        // 3) のしタイプボタンの表示を更新
        this.refreshNoshiTypeButton(targetBase, source.noshiType);

        // 4) プレビュー・編集/削除ボタンの表示を更新
        const productHandle = this.getProductHandleByBase(targetBase);
        this.refreshGiftPreview(
          targetBase,
          'noshi',
          source.uuidNoshi,
          productHandle,
        );
        this.refreshGiftPreview(
          targetBase,
          'msg',
          source.uuidMessage,
          productHandle,
        );
      }
      console.log(
        `のし・メッセージ設定を ${targets.length} 件に反映しました。`,
      );
    } catch (error) {
      console.error('一括反映エラー:', error);
      alert('反映に失敗しました: ' + error.message);
    } finally {
      this.hideLoading();
    }
  }

  // カタログ商品の hidden input に値をセット（クラス名 + base_identifier で特定）
  setCatalogInputValue(baseIdentifier, inputClass, value) {
    const el = document.querySelector(
      `.${inputClass}[data-base-identifier="${baseIdentifier}"]`,
    );
    if (el) el.value = value || '';
  }

  getProductHandleByBase(baseIdentifier) {
    const el = document.querySelector(
      `.base-identifier-input[data-base-identifier="${baseIdentifier}"]`,
    );
    return el ? el.dataset.productHandle : undefined;
  }

  // のしタイプボタンのテキストを noshi_type の値に合わせて更新
  refreshNoshiTypeButton(baseIdentifier, noshiType) {
    const btn = document.querySelector(
      `.noshi-type-select-btn[data-base-identifier="${baseIdentifier}"]`,
    );
    if (!btn) return;
    const btnText = btn.querySelector('.btn-text');
    if (!btnText) return;
    if (noshiType === 'outer') {
      btnText.textContent = 'のしタイプ: 外のし';
    } else if (noshiType === 'inner') {
      btnText.textContent = 'のしタイプ: 内のし';
    } else {
      btnText.textContent = 'のし（熨斗）利用';
    }
  }

  // プレビュー画像と編集/削除ボタンの表示を、与えられた識別子に合わせて更新する。
  // identifier が空ならプレビューを隠してボタンを「選択」状態に戻す。
  refreshGiftPreview(baseIdentifier, editType, identifier, productHandle) {
    const previewId = `${editType === 'noshi' ? 'noshi' : 'message'}-preview-${baseIdentifier}`;
    const previewDiv = document.getElementById(previewId);

    if (!identifier) {
      if (previewDiv) previewDiv.style.display = 'none';
      this.updateButtonDisplay(productHandle, editType, false, baseIdentifier);
      return;
    }

    // 既存のプレビュー表示ロジックを再利用（コピー元と同じ画像を指す）
    this.showPreviewIfNeeded(
      editType,
      identifier,
      productHandle,
      baseIdentifier,
    );
  }

  // 複数のプロパティを1リクエストで更新する（既存プロパティは保持）
  async updateMultipleLineItemProperties(baseIdentifier, propsToSet) {
    const cartResponse = await fetch('/cart.js');
    if (!cartResponse.ok) {
      throw new Error('カート情報の取得に失敗しました');
    }
    const cart = await cartResponse.json();
    const targetItem = cart.items.find(
      (item) => item.properties?._base_identifier === baseIdentifier,
    );
    if (!targetItem) {
      throw new Error(
        `base_identifier ${baseIdentifier} を持つカートアイテムが見つかりません。`,
      );
    }

    const properties = targetItem.properties
      ? { ...targetItem.properties }
      : {};
    Object.keys(propsToSet).forEach((key) => {
      properties[key] = propsToSet[key];
    });

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: targetItem.key, properties }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    return await response.json();
  }

  // 反映確認モーダル（動的生成・インラインスタイル）。Promise<boolean> を返す。
  showReflectConfirmModal(source) {
    return new Promise((resolve) => {
      const items = [];
      if (source.noshiType || source.uuidNoshi) {
        items.push('・のし（熨斗）利用／内のし・外のし指定');
      }
      if (source.uuidNoshi) items.push('・のし（熨斗）の内容');
      if (source.uuidMessage) items.push('・メッセージカードの内容');

      const overlay = document.createElement('div');
      overlay.style.cssText =
        'position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;z-index:10000;';

      const box = document.createElement('div');
      box.style.cssText =
        'background:#fff;max-width:420px;width:90%;border-radius:8px;padding:24px;box-shadow:0 8px 30px rgba(0,0,0,.2);font-size:14px;line-height:1.7;color:#333;';
      box.innerHTML =
        '<h3 style="margin:0 0 12px;font-size:16px;font-weight:bold;">指定した内容を反映しますか？</h3>' +
        '<p style="margin:0 0 8px;">この商品で指定した内容を、カート内のすべてのカタログ商品に反映します。</p>' +
        '<p style="margin:0 0 20px;color:#666;font-size:13px;">※反映後も商品ごとに「変更」することが可能です。</p>' +
        '<div style="display:flex;gap:12px;justify-content:flex-end;">' +
        '<button type="button" class="reflect-cancel" style="padding:8px 20px;border:1px solid #ccc;background:#fff;border-radius:4px;cursor:pointer;">いいえ</button>' +
        '<button type="button" class="reflect-ok" style="padding:8px 20px;border:none;background:#a79362;color:#fff;border-radius:4px;cursor:pointer;">はい</button>' +
        '</div>';

      overlay.appendChild(box);
      document.body.appendChild(overlay);
      document.body.style.overflow = 'hidden';

      const cleanup = (result) => {
        document.body.removeChild(overlay);
        document.body.style.overflow = '';
        resolve(result);
      };

      box
        .querySelector('.reflect-cancel')
        .addEventListener('click', () => cleanup(false));
      box
        .querySelector('.reflect-ok')
        .addEventListener('click', () => cleanup(true));
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) cleanup(false);
      });
    });
  }

  async openExternalEditor(button, editType) {
    const productHandle = button.dataset.productHandle;
    const btnBaseIdentifier = button.dataset.baseIdentifier;

    console.log('=== openExternalEditor ===');
    console.log('editType:', editType);
    console.log('btnBaseIdentifier:', btnBaseIdentifier);

    // 熨斗の場合はnoshi_typeが選択されているかチェック（base_identifierで検索）
    if (editType === 'noshi') {
      if (!btnBaseIdentifier) {
        console.error('base_identifierが見つかりません');
        alert('エラーが発生しました。ページを再読み込みしてください。');
        return;
      }

      const noshiTypeInput = document.querySelector(
        `.noshi-type-input[data-base-identifier="${btnBaseIdentifier}"]`,
      );
      const noshiType = noshiTypeInput ? noshiTypeInput.value : '';

      if (!noshiType || noshiType === '') {
        alert('先に「のし（熨斗）利用」で外のし・内のしを選択してください。');
        return;
      }
    }

    // 対応するUUID inputを取得（base_identifierで検索）
    const uuidSuffix = editType === 'noshi' ? 'noshi' : 'message';
    const uuidInput = document.querySelector(
      `input[name="properties[_uuid_${uuidSuffix}]"][data-base-identifier="${btnBaseIdentifier}"]`,
    );

    // 熨斗・メッセージカードの識別子は base_identifier から派生させず、
    // それぞれ独立に cart-identifier-proxy から採番する（noshi と message で別番号でよい）。
    // 「変更」も含めて常に新しい番号を採番して上書きする（mod=new 固定）。
    // これにより、コピーで複数商品が同じ識別子を共有していても、ある商品で「変更」すると
    // 新しい識別子へ分岐し、他商品の画像（同じ旧識別子）には影響しない。
    const mode = 'new';
    let identifier;

    try {
      const identifierData = await this.generateNewIdentifier();
      identifier =
        identifierData.baseIdentifier + (editType === 'noshi' ? 'n' : 'm');
      console.log(`${editType}識別子を新規採番: ${identifier}`);
    } catch (error) {
      console.error('識別子の採番エラー:', error);
      alert('識別子の取得に失敗しました: ' + error.message);
      return;
    }

    // 旧識別子（現在確定している値）を保持しておく。
    // 画像がECナビ上で確定するまで property は書き換えない（遅延コミット方式）。
    // これにより「変更」でウィンドウを開いても、画像を作らず閉じた場合は旧設定が消えない。
    const oldIdentifier = uuidInput ? uuidInput.value || '' : '';

    if (ECNAVI_CONFIG.testMode) {
      // テストモード: 編集画面を開かずに即座にプレビューを表示
      console.log(`[テストモード] ${editType}編集画面をスキップ`);
      console.log(`パラメータ: mode=${mode}, identifier=${identifier}`);

      // 2秒後にプレビューを表示（編集完了をシミュレート）
      setTimeout(() => {
        this.checkAndDisplayPreviewImage(
          productHandle,
          editType,
          identifier,
          btnBaseIdentifier,
          oldIdentifier,
        );
      }, 2000);

      alert(
        `[テストモード]\n${editType === 'noshi' ? '熨斗' : 'メッセージカード'}編集画面をシミュレート中...\n2秒後にプレビューが表示されます。`,
      );
    } else {
      // 本番モード: 実際にECナビを開く
      // 筆の達人ECナビのパラメータ
      const params = {
        uid: ECNAVI_CONFIG.uid,
        dno: identifier, // サフィックス付き識別子を使用
        ...ECNAVI_CONFIG.defaultParams,
        siz: editType === 'noshi' ? 'A4' : 'カード', // のし紙はA4、メッセージカードはカード
        mod: mode, // 'new' または 'mdf'
        typ: editType === 'noshi' ? 'noshi' : 'msg', // ECナビは 'noshi' または 'msg' を期待
        // retパラメータを削除 - ポップアップ内でのリダイレクトを防ぐため
      };

      // 熨斗の場合はnoshi_typeパラメータを追加
      if (editType === 'noshi') {
        const noshiTypeInput = document.querySelector(
          `.noshi-type-input[data-base-identifier="${btnBaseIdentifier}"]`,
        );
        const noshiType = noshiTypeInput ? noshiTypeInput.value : '';
        if (noshiType) {
          params.noshi_type = noshiType;
        }
      }

      // URLパラメータを構築
      const urlParams = new URLSearchParams(params);
      const editorUrl = `${ECNAVI_CONFIG.baseUrl}?${urlParams.toString()}`;

      // 外部編集アプリを開く
      const windowName = `${params.uid}_${params.dno}`;
      const editorWindow = window.open(
        editorUrl,
        windowName,
        ECNAVI_CONFIG.windowOptions,
      );

      // 編集完了を監視
      this.monitorEditCompletion(
        editorWindow,
        productHandle,
        editType,
        identifier,
        btnBaseIdentifier,
        oldIdentifier,
      );
    }
  }

  monitorEditCompletion(
    editorWindow,
    productHandle,
    editType,
    identifier,
    baseIdentifier,
    oldIdentifier = '',
  ) {
    const checkClosed = () => {
      if (editorWindow.closed) {
        // 編集完了後、プレビュー画像の存在をチェック
        this.checkAndDisplayPreviewImage(
          productHandle,
          editType,
          identifier,
          baseIdentifier,
          oldIdentifier,
        );
      } else {
        // 1秒後に再チェック
        setTimeout(checkClosed, 1000);
      }
    };

    // 初回チェックを開始
    setTimeout(checkClosed, 1000);
  }

  // 編集ウィンドウを閉じた後に呼ばれる。新しく採番した identifier の画像がECナビ上に
  // 出来ているかを確認し、出来ていたら初めて property を新 identifier に確定（コミット）する。
  // 出来ていなければ（＝画像を作らずキャンセル）property は書き換えず、oldIdentifier の
  // 旧設定をそのまま保持・再表示する。これにより「変更→キャンセルで旧設定が消える」を防ぐ。
  checkAndDisplayPreviewImage(
    productHandle,
    editType,
    identifier,
    baseIdentifier,
    oldIdentifier = '',
  ) {
    console.log('=== checkAndDisplayPreviewImage ===');
    console.log('baseIdentifier:', baseIdentifier);
    console.log('identifier:', identifier, '/ oldIdentifier:', oldIdentifier);

    const previewId = `${editType === 'noshi' ? 'noshi' : 'message'}-preview-${baseIdentifier}`;
    const previewDiv = document.getElementById(previewId);
    if (!previewDiv) return;

    const img = previewDiv.querySelector('.preview-img');

    if (ECNAVI_CONFIG.testMode) {
      // テストモード: 画像確認をスキップし「作成された」とみなしてコミット
      this.commitGiftIdentifier(baseIdentifier, editType, identifier)
        .then(() => {
          const placeholderText =
            editType === 'noshi' ? '熨斗プレビュー' : 'メッセージカード';
          const textColor = editType === 'noshi' ? '8c0a00' : '0066cc';
          img.src = `https://placehold.jp/20/${textColor}/ffffff/600x300.png?text=${encodeURIComponent(placeholderText)}\n${encodeURIComponent('識別子: ' + identifier)}`;
          previewDiv.style.display = 'block';
          this.updateButtonDisplay(
            productHandle,
            editType,
            true,
            baseIdentifier,
          );
        })
        .catch((err) => console.error('識別子のコミットに失敗:', err));
      return;
    }

    // 本番モード: 新 identifier の画像が存在するか確認
    const imageUrl = `${ECNAVI_CONFIG.imageBaseUrl}${ECNAVI_CONFIG.uid}_${identifier}.png`;
    const testImg = new Image();

    testImg.onload = async () => {
      // 画像が確定 → ここで初めて property を新 identifier に上書き（コミット）
      try {
        await this.commitGiftIdentifier(baseIdentifier, editType, identifier);
      } catch (err) {
        console.error('識別子のコミットに失敗:', err);
      }
      const timestamp = new Date().getTime();
      img.src = `${imageUrl}?tim=${timestamp}`;
      previewDiv.style.display = 'block';
      console.log(
        `${editType === 'noshi' ? '熨斗' : 'メッセージカード'}を新規作成・確定しました: ${identifier}`,
      );
      this.updateButtonDisplay(productHandle, editType, true, baseIdentifier);
    };

    testImg.onerror = () => {
      // 画像が作られなかった（キャンセル）→ property は触らず旧設定を保持する
      console.log(
        `${editType === 'noshi' ? '熨斗' : 'メッセージカード'}の画像が作成されませんでした。旧設定を保持します（old: ${oldIdentifier || 'なし'}）。`,
      );

      if (oldIdentifier) {
        // 旧識別子のプレビューを再表示（旧画像は存在するはず）
        this.showPreviewIfNeeded(
          editType,
          oldIdentifier,
          productHandle,
          baseIdentifier,
        );
      } else {
        // もともと未設定だった場合はプレビューを隠して「選択」状態に戻す
        previewDiv.style.display = 'none';
        this.updateButtonDisplay(
          productHandle,
          editType,
          false,
          baseIdentifier,
        );
      }
    };

    testImg.src = `${imageUrl}?tim=${new Date().getTime()}`;
  }

  // 採番した識別子を property（と hidden input）に確定的に書き込む。
  // 画像の存在がECナビ上で確認できた時にのみ呼ぶ（遅延コミット）。
  async commitGiftIdentifier(baseIdentifier, editType, identifier) {
    const uuidSuffix = editType === 'noshi' ? 'noshi' : 'message';
    const uuidInput = document.querySelector(
      `input[name="properties[_uuid_${uuidSuffix}]"][data-base-identifier="${baseIdentifier}"]`,
    );
    if (uuidInput) {
      uuidInput.value = identifier;
    }
    await this.updateSingleLineItemProperty(
      baseIdentifier,
      `uuid_${uuidSuffix}`,
      identifier,
    );
    console.log(`${editType}識別子を確定（コミット）: ${identifier}`);
  }

  // 熨斗・メッセージカードの指定をこの商品から外す（プロパティのみクリア・元画像は残す）。
  //
  // 重要: ECナビの削除API（mod=del）は呼ばない。コピー機能で複数のカタログ商品が
  // 同じ識別子（＝同じECナビ画像）を共有し得るため、元画像を削除すると他商品の
  // プレビュー・印刷参照まで巻き込んで全滅してしまう。
  // よって _uuid_noshi / _uuid_message を空文字にするだけにし、元画像はそのまま残す。
  //
  // 確認ダイアログやローディング表示は呼び出し側の責務（deleteGiftOption / のしタイプ確定）。
  async clearGiftOptionProperty(baseIdentifier, editType, productHandle) {
    const uuidSuffix = editType === 'noshi' ? 'noshi' : 'message';

    // この商品の property のみクリア
    await this.updateSingleLineItemProperty(
      baseIdentifier,
      `uuid_${uuidSuffix}`,
      '',
    );

    // UI を更新
    const uuidInput = document.querySelector(
      `input[name="properties[_uuid_${uuidSuffix}]"][data-base-identifier="${baseIdentifier}"]`,
    );
    if (uuidInput) {
      uuidInput.value = '';
    }
    const previewDiv = document.getElementById(
      `${editType === 'noshi' ? 'noshi' : 'message'}-preview-${baseIdentifier}`,
    );
    if (previewDiv) {
      previewDiv.style.display = 'none';
    }
    this.updateButtonDisplay(productHandle, editType, false, baseIdentifier);

    console.log(
      `${editType === 'noshi' ? '熨斗' : 'メッセージカード'}を削除（プロパティのみクリア・元画像は保持）: ${baseIdentifier}`,
    );
  }

  // 削除ボタン（「のし（熨斗）削除」「メッセージカード削除」）から呼ばれる。
  async deleteGiftOption(button, editType) {
    const productHandle = button.dataset.productHandle;
    const baseIdentifier = button.dataset.baseIdentifier;

    console.log('=== deleteGiftOption ===');
    console.log('editType:', editType);
    console.log('baseIdentifier:', baseIdentifier);

    // 対応するUUID inputを取得（base_identifierで検索）
    const uuidSuffix = editType === 'noshi' ? 'noshi' : 'message';
    const uuidInput = document.querySelector(
      `input[name="properties[_uuid_${uuidSuffix}]"][data-base-identifier="${baseIdentifier}"]`,
    );

    if (!uuidInput || !uuidInput.value) {
      return;
    }

    if (
      !confirm(
        `この${editType === 'noshi' ? '熨斗' : 'メッセージカード'}を削除しますか？\n（この商品からの指定を外します。同じ内容を反映した他の商品には影響しません。）`,
      )
    ) {
      return;
    }

    this.showLoading();
    try {
      await this.clearGiftOptionProperty(
        baseIdentifier,
        editType,
        productHandle,
      );
    } catch (error) {
      console.error('削除エラー:', error);
      alert('削除に失敗しました: ' + error.message);
    } finally {
      this.hideLoading();
    }
  }

  updateButtonDisplay(productHandle, editType, hasGift, baseIdentifier) {
    if (!baseIdentifier) {
      console.warn('updateButtonDisplay: baseIdentifierが指定されていません');
      return;
    }

    const editBtn = document.querySelector(
      `.${editType === 'noshi' ? 'noshi' : 'message'}-edit-btn[data-base-identifier="${baseIdentifier}"]`,
    );
    const deleteBtn = document.querySelector(
      `.${editType === 'noshi' ? 'noshi' : 'message'}-delete-btn[data-base-identifier="${baseIdentifier}"]`,
    );

    if (editBtn && deleteBtn) {
      const btnText = editBtn.querySelector('.btn-text');
      if (hasGift) {
        btnText.textContent =
          editType === 'noshi' ? 'のし（熨斗）変更' : 'メッセージカード変更';
        deleteBtn.style.display = 'inline-flex';
      } else {
        btnText.textContent =
          editType === 'noshi' ? 'のし（熨斗）選択' : 'メッセージカード選択';
        deleteBtn.style.display = 'none';
      }
    } else {
      console.warn('updateButtonDisplay: ボタンが見つかりません', {
        baseIdentifier,
        productHandle,
        editType,
      });
    }
  }

  checkExistingPreviews() {
    console.log('=== checkExistingPreviews実行 ===');

    // UUID識別子から編集済みアイテムをチェック
    const uuidNoshiInputs = document.querySelectorAll('.uuid-noshi-input');
    const uuidMessageInputs = document.querySelectorAll('.uuid-message-input');

    // console.log(`熨斗UUID input数: ${uuidNoshiInputs.length}`);
    // console.log(`メッセージUUID input数: ${uuidMessageInputs.length}`);

    // 熨斗プレビューをチェック
    uuidNoshiInputs.forEach((input) => {
      const baseIdentifier = input.dataset.baseIdentifier;
      const productHandle = input.dataset.productHandle;
      const noshiIdentifier = input.value;

      // console.log('熨斗input - baseIdentifier:', baseIdentifier);

      if (noshiIdentifier && noshiIdentifier !== '') {
        // console.log(
        //   `熨斗プレビューチェック: 識別子: ${noshiIdentifier}, baseIdentifier: ${baseIdentifier}`,
        // );
        this.showPreviewIfNeeded(
          'noshi',
          noshiIdentifier,
          productHandle,
          baseIdentifier,
        );
      }
    });

    // メッセージプレビューをチェック
    uuidMessageInputs.forEach((input) => {
      const baseIdentifier = input.dataset.baseIdentifier;
      const productHandle = input.dataset.productHandle;
      const messageIdentifier = input.value;

      // console.log('メッセージinput - baseIdentifier:', baseIdentifier);

      if (messageIdentifier && messageIdentifier !== '') {
        // console.log(
        //   `メッセージプレビューチェック: 識別子: ${messageIdentifier}, baseIdentifier: ${baseIdentifier}`,
        // );
        this.showPreviewIfNeeded(
          'msg',
          messageIdentifier,
          productHandle,
          baseIdentifier,
        );
      }
    });

    console.log('=== checkExistingPreviews完了 ===');
  }

  showPreviewIfNeeded(editType, identifier, productHandle, baseIdentifier) {
    console.log('=== showPreviewIfNeeded ===');
    // console.log('baseIdentifier:', baseIdentifier);
    // console.log('identifier:', identifier);

    const previewId = `${editType === 'noshi' ? 'noshi' : 'message'}-preview-${baseIdentifier}`;
    const previewDiv = document.getElementById(previewId);

    if (previewDiv) {
      const img = previewDiv.querySelector('.preview-img');

      if (ECNAVI_CONFIG.testMode) {
        // テストモード: プレースホルダー画像を使用
        const placeholderText =
          editType === 'noshi' ? '熨斗プレビュー' : 'メッセージカード';
        const textColor = editType === 'noshi' ? '8c0a00' : '0066cc';
        img.src = `https://placehold.jp/20/${textColor}/ffffff/600x300.png?text=${encodeURIComponent(placeholderText)}\n${encodeURIComponent('識別子: ' + identifier)}`;
        previewDiv.style.display = 'block';
        console.log(
          `テストモード: ${editType}プレビューを表示 識別子: ${identifier}`,
        );

        // ボタンの表示を更新
        this.updateButtonDisplay(productHandle, editType, true, baseIdentifier);
      } else {
        // 本番モード: 実際の画像を確認
        const imageUrl = `${ECNAVI_CONFIG.imageBaseUrl}${ECNAVI_CONFIG.uid}_${identifier}.png`;

        // 画像の存在確認
        const testImg = new Image();

        testImg.onload = () => {
          // 画像が存在する場合、プレビューを表示
          const timestamp = new Date().getTime();
          img.src = `${imageUrl}?tim=${timestamp}`;
          previewDiv.style.display = 'block';
          // console.log(`${editType}プレビューを表示: ${identifier}`);

          // ボタンの表示を更新
          this.updateButtonDisplay(
            productHandle,
            editType,
            true,
            baseIdentifier,
          );
        };

        testImg.onerror = async () => {
          // 画像が存在しない場合、残留しているUUIDプロパティをクリアする
          // （カート初期化時の defense-in-depth: 過去のセッションで残ったstale UUIDを自動クリーンアップ）
          console.log(
            `${editType === 'noshi' ? '熨斗' : 'メッセージカード'}プレビュー画像が見つかりません: ${identifier}。UUIDをクリアします。`,
          );
          previewDiv.style.display = 'none';

          // UUID input 要素を取得してクリア
          const uuidSuffix = editType === 'noshi' ? 'noshi' : 'message';
          const uuidInput = document.querySelector(
            `input[name="properties[_uuid_${uuidSuffix}]"][data-base-identifier="${baseIdentifier}"]`,
          );
          if (uuidInput) {
            uuidInput.value = '';
          }

          // カートの Line Item Property をクリア
          try {
            await this.updateSingleLineItemProperty(
              baseIdentifier,
              `uuid_${uuidSuffix}`,
              '',
            );
            console.log(
              `[カート初期化] ${editType === 'noshi' ? '熨斗' : 'メッセージカード'}のstale UUIDプロパティをクリアしました。`,
            );
          } catch (err) {
            console.error('UUIDプロパティのクリアに失敗:', err);
          }

          // ボタンの表示を「未設定」に更新
          this.updateButtonDisplay(
            productHandle,
            editType,
            false,
            baseIdentifier,
          );
        };

        // 画像の存在チェックを開始
        testImg.src = `${imageUrl}?tim=${new Date().getTime()}`;
      }
    } else {
      console.log(`プレビューDiv見つからず: ${previewId}`);
    }
  }

  setupDeliveryDateLimits() {
    const dateSelect = document.getElementById('delivery-date');
    if (!dateSelect) return;

    // 曜日の配列
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];

    // 今日の日付を取得
    const today = new Date();

    // 日付を YYYY/MM/DD(曜日) 形式に変換
    const formatDateWithWeekday = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const weekday = weekdays[date.getDay()];
      return `${year}/${month}/${day}(${weekday})`;
    };

    // 現在のカート属性の値を保存
    const currentValue =
      dateSelect.value ||
      (this.cartData && this.cartData.attributes
        ? this.cartData.attributes.delivery_date
        : '');

    // 既存のオプションをクリア（最初の「指定なし」オプションは残す）
    while (dateSelect.options.length > 1) {
      dateSelect.remove(1);
    }

    // 4日後から21日先までの日付オプションを生成
    for (let i = 4; i <= 21; i++) {
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + i);

      const option = document.createElement('option');
      const dateString = formatDateWithWeekday(targetDate);
      option.value = dateString;
      option.textContent = dateString;

      // 現在の値と一致する場合は選択状態にする
      if (currentValue === dateString) {
        option.selected = true;
      }

      dateSelect.appendChild(option);
    }

    // デフォルトで「指定なし」が選択されていない場合は、それを選択
    if (!currentValue || currentValue === '' || currentValue === '指定なし') {
      dateSelect.value = '指定なし';
    }

    // 日付が変更された時の処理
    dateSelect.addEventListener('change', (e) => {
      console.log('配送日が変更されました:', e.target.value);
    });

    // 時間選択の変更も監視（属性として保存されるため）
    const timeSelect = document.getElementById('delivery-time');
    if (timeSelect) {
      timeSelect.addEventListener('change', () => {
        console.log('配送時間が変更されました:', timeSelect.value);
      });
    }
  }

  setupWrappingSelection() {
    const modal = document.getElementById('wrapping-modal');
    const modalOverlay = modal.querySelector('.wrapping-modal__overlay');
    const modalClose = modal.querySelector('.wrapping-modal__close');
    const modalCancel = modal.querySelector('.wrapping-modal__cancel');
    const modalConfirm = modal.querySelector('.wrapping-modal__confirm');
    const wrappingOptions = modal.querySelectorAll('.wrapping-option');
    const wrappingButtons = document.querySelectorAll('.wrapping-select-btn');

    console.log(
      '###################### setupWrappingSelection ##########################',
    );
    // console.log('包装紙ボタン数:', wrappingButtons.length);
    // console.log('モーダル要素:', modal);
    // console.log('包装紙オプション数:', wrappingOptions.length);

    // // 各ボタンの詳細をログ出力
    // wrappingButtons.forEach((btn, index) => {
    //   console.log(`包装紙ボタン ${index}:`, btn.dataset);
    // });

    let currentProductHandle = null;
    let currentBaseIdentifier = null;
    let selectedWrapping = null;

    // 包装紙選択ボタンのクリックイベント
    wrappingButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        currentProductHandle = btn.dataset.productHandle;
        currentBaseIdentifier = btn.dataset.baseIdentifier;

        console.log('currentBaseIdentifier:', currentBaseIdentifier);

        // 現在の選択状態を取得（base_identifierで検索）
        if (!currentBaseIdentifier) {
          console.error('base_identifierが見つかりません');
          alert('エラーが発生しました。ページを再読み込みしてください。');
          return;
        }

        const wrappingInput = document.querySelector(
          `input[name="properties[_wrapping]"][data-base-identifier="${currentBaseIdentifier}"]`,
        );
        const currentValue = wrappingInput ? wrappingInput.value : '';

        // モーダル内の選択状態をリセット
        wrappingOptions.forEach((opt) => {
          opt.classList.remove('is-selected');
          if (opt.dataset.wrappingName === currentValue) {
            opt.classList.add('is-selected');
            selectedWrapping = currentValue;
          }
        });

        // 確認ボタンの状態を更新
        modalConfirm.disabled = !selectedWrapping;

        // モーダルを開く
        modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      });
    });

    // 包装紙オプションのクリックイベント
    wrappingOptions.forEach((option) => {
      option.addEventListener('click', () => {
        // 選択状態を更新
        wrappingOptions.forEach((opt) => opt.classList.remove('is-selected'));
        option.classList.add('is-selected');
        selectedWrapping = option.dataset.wrappingName;

        // 確認ボタンを有効化
        modalConfirm.disabled = false;
      });
    });

    // モーダルを閉じる関数
    const closeModal = () => {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
      selectedWrapping = null;
      currentProductHandle = null;
      currentBaseIdentifier = null;
    };

    // 閉じるボタンのイベント
    modalOverlay.addEventListener('click', closeModal);
    modalClose.addEventListener('click', closeModal);
    modalCancel.addEventListener('click', closeModal);

    // 確認ボタンのクリックイベント
    modalConfirm.addEventListener('click', async () => {
      if (
        !selectedWrapping ||
        !currentProductHandle ||
        !currentBaseIdentifier
      ) {
        console.log('必要な情報が不足しています');
        return;
      }

      // 確認ボタンを無効化して二重クリックを防止
      modalConfirm.disabled = true;
      // ローディング表示（ネットワーク更新中であることをユーザーに伝える）
      this.showLoading();

      try {
        // 対応するinput要素を更新（base_identifierで検索）
        const wrappingInput = document.querySelector(
          `input[name="properties[_wrapping]"][data-base-identifier="${currentBaseIdentifier}"]`,
        );

        if (wrappingInput) {
          wrappingInput.value = selectedWrapping;

          // ボタンのテキストを更新
          const btn = document.querySelector(
            `.wrapping-select-btn[data-base-identifier="${currentBaseIdentifier}"]`,
          );
          if (btn) {
            const btnText = btn.querySelector('.btn-text');
            if (selectedWrapping === 'なし') {
              btnText.textContent = '包装紙: 包装なし';
            } else {
              btnText.textContent = `包装紙: ${selectedWrapping}`;
            }
          } else {
            console.error('包装紙ボタンが見つかりません');
          }

          // line item property を更新（ネットワーク往復が発生する）
          console.log('line item propertyを更新開始');
          await this.updateSingleLineItemProperty(
            currentBaseIdentifier,
            `wrapping`,
            selectedWrapping,
          );
          console.log('line item property更新完了');
        } else {
          console.error('包装紙input要素が見つかりません');
        }

        // モーダルを閉じる
        closeModal();
      } catch (error) {
        console.error('包装紙更新エラー:', error);
        alert('包装紙の更新に失敗しました: ' + error.message);
        // エラー時はボタンを再有効化
        modalConfirm.disabled = false;
      } finally {
        // ローディングを非表示
        this.hideLoading();
      }
    });

    // 既存の包装紙選択を表示
    this.displayExistingWrappingSelections();
    // 既存ののし（熨斗）利用を表示
    this.displayExistingNoshiTypeSelections();
  }

  openNoshiTypeModal(buttonElement) {
    const modal = document.getElementById('noshi-modal');
    const modalOverlay = modal.querySelector('.noshi-modal__overlay');
    const modalClose = modal.querySelector('.noshi-modal__close');
    const modalCancel = modal.querySelector('.noshi-modal__cancel');
    const modalConfirm = modal.querySelector('.noshi-modal__confirm');
    const noshiOptions = modal.querySelectorAll('.noshi-option');

    // ボタン要素から直接データを取得
    const baseIdentifier = buttonElement.dataset.baseIdentifier;
    const productHandle = buttonElement.dataset.productHandle;

    // console.log('=== のしタイプモーダル開く ===');
    // console.log('buttonElement:', buttonElement);
    // console.log('baseIdentifier:', baseIdentifier);
    // console.log('productHandle:', productHandle);

    // 現在のnoshi type設定を取得（base_identifierで検索）
    if (!baseIdentifier) {
      console.error('base_identifierが見つかりません');
      alert('エラーが発生しました。ページを再読み込みしてください。');
      return;
    }

    const noshiTypeInput = document.querySelector(
      `.noshi-type-input[data-base-identifier="${baseIdentifier}"]`,
    );
    const currentNoshiType = noshiTypeInput ? noshiTypeInput.value || '' : '';
    console.log('currentNoshiType:', currentNoshiType);

    // 風呂敷商品が選択されているかチェック
    const cartItems = this.cartData ? this.cartData.items : [];
    const hasFuroshiki = cartItems.some(
      (item) =>
        item.properties?._is_catalog_furoshiki === 'true' &&
        item.properties?._parent_catalog_identifier === baseIdentifier,
    );

    console.log('風呂敷商品が選択されているか:', hasFuroshiki);

    // 現在の選択状態を反映
    noshiOptions.forEach((option) => {
      const optionType = option.dataset.noshiType;
      if (optionType === currentNoshiType) {
        option.classList.add('selected');
      } else {
        option.classList.remove('selected');
      }

      // 風呂敷がある場合、外のしオプションを無効化
      if (hasFuroshiki && optionType === 'outer') {
        option.classList.add('disabled');
        console.log('外のしオプションを無効化しました');
      } else {
        option.classList.remove('disabled');
      }
    });

    // 選択状態に応じて確認ボタンを有効化
    modalConfirm.disabled = !document.querySelector('.noshi-option.selected');

    // モーダルを開く
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // モーダルを閉じる関数
    const closeModal = () => {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
      // 選択状態をクリア
      noshiOptions.forEach((option) => option.classList.remove('selected'));
      modalConfirm.disabled = true;
    };

    // イベントリスナーを一旦削除して重複を防ぐ
    const newModalOverlay = modalOverlay.cloneNode(true);
    const newModalClose = modalClose.cloneNode(true);
    const newModalCancel = modalCancel.cloneNode(true);
    const newModalConfirm = modalConfirm.cloneNode(true);

    modalOverlay.parentNode.replaceChild(newModalOverlay, modalOverlay);
    modalClose.parentNode.replaceChild(newModalClose, modalClose);
    modalCancel.parentNode.replaceChild(newModalCancel, modalCancel);
    modalConfirm.parentNode.replaceChild(newModalConfirm, modalConfirm);

    // 新しい要素への参照を更新
    const updatedModal = document.getElementById('noshi-modal');
    const updatedOverlay = updatedModal.querySelector('.noshi-modal__overlay');
    const updatedClose = updatedModal.querySelector('.noshi-modal__close');
    const updatedCancel = updatedModal.querySelector('.noshi-modal__cancel');
    const updatedConfirm = updatedModal.querySelector('.noshi-modal__confirm');
    const updatedNoshiOptions = updatedModal.querySelectorAll('.noshi-option');

    // オーバーレイクリックでモーダルを閉じる
    updatedOverlay.addEventListener('click', closeModal);

    // 閉じるボタン
    updatedClose.addEventListener('click', closeModal);

    // キャンセルボタン
    updatedCancel.addEventListener('click', closeModal);

    // オプション選択
    updatedNoshiOptions.forEach((option) => {
      option.addEventListener('click', () => {
        // 無効化されているオプションは選択できない
        if (option.classList.contains('disabled')) {
          console.log('このオプションは無効化されています');
          return;
        }

        // 他の選択を解除
        updatedNoshiOptions.forEach((opt) => opt.classList.remove('selected'));
        // 選択状態を追加
        option.classList.add('selected');
        // 確認ボタンを有効化
        updatedConfirm.disabled = false;
      });
    });

    // 確認ボタン
    updatedConfirm.addEventListener('click', async () => {
      const selectedOption = updatedModal.querySelector(
        '.noshi-option.selected',
      );
      if (selectedOption) {
        const selectedNoshiType = selectedOption.dataset.noshiType;

        // 「選択無し」を選び、かつ熨斗が既に作成済みの場合は、熨斗も連動削除する。
        // 削除を伴う場合のみ確認ダイアログを出す（熨斗が無ければ確認なしで素通り）。
        const uuidNoshiInput = document.querySelector(
          `input[name="properties[_uuid_noshi]"][data-base-identifier="${baseIdentifier}"]`,
        );
        const shouldDeleteNoshi =
          selectedNoshiType === '' &&
          !!(uuidNoshiInput && uuidNoshiInput.value);
        if (shouldDeleteNoshi) {
          if (
            !confirm(
              'のし（熨斗）利用をやめると、設定済みの熨斗も削除されます。よろしいですか？',
            )
          ) {
            // モーダルは開いたまま・選択状態も維持してキャンセル
            return;
          }
        }

        // 確認ボタンを無効化して二重クリックを防止
        updatedConfirm.disabled = true;
        // ローディング表示（ネットワーク更新中であることをユーザーに伝える）
        this.showLoading();

        try {
          if (!baseIdentifier) {
            throw new Error('識別子が見つかりません');
          }

          // noshi typeをhidden inputに設定（base_identifierで検索）
          const currentNoshiTypeInput = document.querySelector(
            `.noshi-type-input[data-base-identifier="${baseIdentifier}"]`,
          );
          if (currentNoshiTypeInput) {
            currentNoshiTypeInput.value = selectedNoshiType;
            console.log('noshi type input更新完了:', selectedNoshiType);
          } else {
            console.error('noshi type inputが見つかりません');
          }

          // ボタンのテキストを更新（クロージャーでbuttonElementを参照）
          if (buttonElement) {
            const btnText = buttonElement.querySelector('.btn-text');
            if (selectedNoshiType === '') {
              btnText.textContent = 'のし（熨斗）利用';
            } else if (selectedNoshiType === 'outer') {
              btnText.textContent = 'のしタイプ: 外のし';
            } else if (selectedNoshiType === 'inner') {
              btnText.textContent = 'のしタイプ: 内のし';
            }
          }

          // line item property を更新（ネットワーク往復が発生する）
          console.log('のしタイプ更新開始:', baseIdentifier, selectedNoshiType);
          await this.updateSingleLineItemProperty(
            baseIdentifier,
            'noshi_type',
            selectedNoshiType,
          );
          console.log('のしタイプ更新完了');

          // 「選択無し」かつ熨斗ありの場合は、熨斗も連動削除（プロパティのみクリア・元画像は残す）
          if (shouldDeleteNoshi) {
            const productHandle = buttonElement
              ? buttonElement.dataset.productHandle
              : undefined;
            await this.clearGiftOptionProperty(
              baseIdentifier,
              'noshi',
              productHandle,
            );
            console.log('のし利用解除に伴い熨斗を連動削除しました');
          }

          // モーダルを閉じる
          closeModal();
        } catch (error) {
          console.error('のしタイプ更新エラー:', error);
          alert('のしタイプの更新に失敗しました: ' + error.message);
          // エラー時はボタンを再有効化
          updatedConfirm.disabled = false;
        } finally {
          // ローディングを非表示
          this.hideLoading();
        }
      }
    });
  }

  displayExistingNoshiTypeSelections() {
    console.log('=== displayExistingNoshiTypeSelections開始 ===');
    const noshiTypeButtons = document.querySelectorAll(
      '.noshi-type-select-btn',
    );
    const cartItems = this.cartData ? this.cartData.items : [];

    // console.log('のしタイプボタン数:', noshiTypeButtons.length);
    // console.log('カートアイテム数:', cartItems.length);

    noshiTypeButtons.forEach((btn) => {
      const baseIdentifier = btn.dataset.baseIdentifier;
      // console.log('処理中 - baseIdentifier:', baseIdentifier);

      // 対応するcart itemを探す（base_identifierで検索）
      const cartItem = baseIdentifier
        ? cartItems.find(
            (item) =>
              item.properties &&
              item.properties._base_identifier === baseIdentifier,
          )
        : null;
      // console.log('見つかったカートアイテム:', cartItem);

      if (cartItem && cartItem.properties) {
        // console.log('カートアイテムプロパティ:', cartItem.properties);
        const noshiType = cartItem.properties._noshi_type;
        // console.log('のしタイプ値:', noshiType);

        if (noshiType) {
          const btnText = btn.querySelector('.btn-text');
          // console.log('ボタンテキスト要素:', btnText);
          if (noshiType === 'outer') {
            btnText.textContent = 'のしタイプ: 外のし';
            console.log('外のしに設定');
          } else if (noshiType === 'inner') {
            btnText.textContent = 'のしタイプ: 内のし';
            console.log('内のしに設定');
          } else {
            btnText.textContent = 'のし（熨斗）利用';
            console.log('選択なしに設定');
          }

          // hidden inputの値も更新（base_identifierで検索）
          const noshiTypeInput = document.querySelector(
            `.noshi-type-input[data-base-identifier="${baseIdentifier}"]`,
          );
          if (noshiTypeInput) {
            noshiTypeInput.value = noshiType;
            console.log('input要素も更新:', noshiType);
          } else {
            console.error('noshi type inputが見つかりません');
          }
        } else {
          console.log('のしタイプ値が空です');
        }
      } else {
        console.log('カートアイテムまたはプロパティが見つかりません');
      }
    });
  }

  displayExistingWrappingSelections() {
    const wrappingButtons = document.querySelectorAll('.wrapping-select-btn');
    const cartItems = this.cartData ? this.cartData.items : [];

    // console.log('=== displayExistingWrappingSelections開始 ===');
    // console.log('包装紙ボタン数:', wrappingButtons.length);

    wrappingButtons.forEach((btn) => {
      const baseIdentifier = btn.dataset.baseIdentifier;
      // console.log('処理中 - baseIdentifier:', baseIdentifier);

      // 対応するcart itemを探す（base_identifierで検索）
      const cartItem = baseIdentifier
        ? cartItems.find(
            (item) =>
              item.properties &&
              item.properties._base_identifier === baseIdentifier,
          )
        : null;

      if (cartItem && cartItem.properties) {
        const wrappingValue = cartItem.properties._wrapping;
        console.log('包装紙値:', wrappingValue);

        if (wrappingValue && wrappingValue !== 'なし') {
          // hidden inputの値も更新
          const wrappingInput = document.querySelector(
            `.wrapping-input[data-base-identifier="${baseIdentifier}"]`,
          );
          if (wrappingInput) {
            wrappingInput.value = wrappingValue;
          } else {
            console.error('wrapping inputが見つかりません');
          }

          // ボタンのテキストを更新
          const btnText = btn.querySelector('.btn-text');
          if (btnText) {
            btnText.textContent = `包装紙: ${wrappingValue}`;
            console.log('ボタンテキスト更新:', wrappingValue);
          }
        }
      }
    });
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  console.log(
    '==================== DOMContentLoaded イベント発火 ====================',
  );
  console.log('DOM状態:', document.readyState);

  // CART_DATAの可用性をチェック
  if (!window.CART_DATA) {
    console.warn(
      'CART_DATA が利用できません。main-cart.liquid 以外のページかもしれません。',
    );
    return;
  }

  // console.log('CART_DATA:', window.CART_DATA);

  const cartElement = document.querySelector('.p-cart');
  // console.log('カート要素(.p-cart):', cartElement);

  if (cartElement) {
    console.log('ShoppingCartクラスを初期化中...');
    const shoppingCart = new ShoppingCart();
    console.log('ShoppingCart初期化完了:', shoppingCart);

    // デバッグ: cart.jsから現在のカート情報を取得
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        console.log('=== cart.jsから取得したカート情報 ===');
        console.log('カートアイテム全体:', cart.items);
        // console.log('各アイテムのline item properties:');
        cart.items.forEach((item, index) => {
          // console.log(`アイテム ${index + 1}: ${item.product_title}`);
          // console.log('  Properties:', item.properties);
          if (item.properties) {
            Object.keys(item.properties).forEach((key) => {
              if (
                key.includes('uuid_') ||
                key.includes('noshi_type') ||
                key.includes('wrapping')
              ) {
                console.log(`    ${key}: ${item.properties[key]}`);
              }
            });
          }
        });
        console.log('================================');
      })
      .catch((error) => console.error('カート情報取得エラー:', error));
  } else {
    console.log(
      'カート要素が見つかりません - ShoppingCartを初期化しませんでした',
    );
  }

  console.log(
    '==================== DOMContentLoaded処理完了 ====================',
  );
});
